import React from 'react';
import { FileText, AlertTriangle } from 'lucide-react';
import Header from '../components/Layout/Header';
import Footer from '../components/Layout/Footer';
import RichText from '../components/content/RichText';
import usePageMeta from '../lib/usePageMeta';

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

function Block({ block }) {
  if (block.p) return <p className="text-gray-700 leading-relaxed"><RichText text={block.p} /></p>;
  if (block.list) {
    return (
      <ul className="list-disc pl-5 space-y-2 text-gray-700 leading-relaxed marker:text-purple-400">
        {block.list.map((item) => <li key={item}><RichText text={item} /></li>)}
      </ul>
    );
  }
  if (block.table) {
    return (
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full min-w-[32rem] text-sm text-left border-collapse">
          <thead>
            <tr>
              {block.table.head.map((h) => (
                <th key={h} scope="col" className="py-2.5 px-3 bg-gray-50 font-semibold text-gray-900 border-b border-gray-200 first:rounded-tl-lg last:rounded-tr-lg">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.table.rows.map((row) => (
              <tr key={row.join('|')} className="align-top">
                {row.map((cell, i) => (
                  <td key={i} className={`py-2.5 px-3 border-b border-gray-100 text-gray-700 ${i === 0 ? 'font-medium text-gray-900 break-words' : ''}`}><RichText text={cell} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  return null;
}

function ReviewNote({ text }) {
  return (
    <aside className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
      <div>
        <p className="font-semibold">For review</p>
        <p className="mt-0.5 leading-relaxed">{text}</p>
      </div>
    </aside>
  );
}

/** A long-form policy page (Privacy, Terms, Cookies) rendered from content/sitePages.js. */
export default function PolicyPage({ doc }) {
  usePageMeta(doc.title, doc.description, { path: `/${doc.slug}` });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1">
        <section className="bg-white border-b">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 rounded-full px-3 py-1 text-xs font-semibold mb-4">
              <FileText className="w-3.5 h-3.5" aria-hidden="true" /> Legal
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">{doc.title}</h1>
            <p className="mt-3 text-sm text-gray-500">Last updated: {formatDate(doc.lastUpdated)}</p>
            {doc.draft && (
              <p className="mt-5 inline-flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-900">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" aria-hidden="true" />
                <span>Draft — this page is awaiting legal review and may change.</span>
              </p>
            )}
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 lg:py-14 lg:grid lg:grid-cols-[14rem_1fr] lg:gap-12">
          <nav aria-label="On this page" className="mb-8 lg:mb-0">
            <div className="lg:sticky lg:top-24 rounded-2xl bg-white border border-gray-100 p-4 lg:p-0 lg:bg-transparent lg:border-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">On this page</p>
              <ol className="space-y-2 text-sm">
                {doc.sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-gray-600 hover:text-purple-700">{s.heading}</a>
                  </li>
                ))}
              </ol>
            </div>
          </nav>

          <article className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-10 space-y-10 min-w-0">
            {doc.intro.length > 0 && (
              <div className="space-y-4">
                {doc.intro.map((p) => <p key={p} className="text-lg text-gray-700 leading-relaxed"><RichText text={p} /></p>)}
              </div>
            )}
            {doc.sections.map((s) => (
              <section key={s.id} aria-labelledby={s.id} className="space-y-4">
                <h2 id={s.id} className="text-xl font-bold text-gray-900 scroll-mt-24">{s.heading}</h2>
                {s.blocks.map((b, i) => <Block key={i} block={b} />)}
                {doc.draft && s.review && <ReviewNote text={s.review} />}
              </section>
            ))}
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
}
